import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";

import { groupTools } from "../utils/HelperFunction/mcpHelpers";

const McpCreateKeyDialog = ({
  open,
  onClose,
  onSubmit,
  submitting = false,
  labels,
  tools = [],
  toolsLoading = false,
  toolsFallback = false,
  expiryOptions = [],
  defaultExpiry = null,
}) => {
  const [name, setName] = useState("");
  const [expiry, setExpiry] = useState(defaultExpiry);
  const [selected, setSelected] = useState([]);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setExpiry(defaultExpiry);
      setTouched(false);
    }
  }, [open, defaultExpiry]);

  const toolNamesKey = tools.map((tool) => tool.name).join("|");
  const toolNames = useMemo(
    () => (toolNamesKey ? toolNamesKey.split("|") : []),
    [toolNamesKey],
  );

  useEffect(() => {
    if (open) setSelected(toolNames);
  }, [open, toolNames]);

  const grouped = useMemo(() => groupTools(tools), [tools]);

  const chosen = useMemo(
    () => toolNames.filter((toolName) => selected.includes(toolName)),
    [toolNames, selected],
  );
  const nameInvalid = touched && !name.trim();
  const toolsInvalid = touched && chosen.length === 0;

  const toggleTool = (toolName) =>
    setSelected((current) =>
      current.includes(toolName)
        ? current.filter((n) => n !== toolName)
        : [...current, toolName],
    );

  const handleSubmit = () => {
    setTouched(true);
    if (!name.trim() || chosen.length === 0) return;

    onSubmit({ name: name.trim(), tools: chosen, expiresInDays: expiry });
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth='sm'
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: "bold", color: "#003D78" }}>
        {labels.title}
      </DialogTitle>

      <DialogContent dividers>
        <TextField
          autoFocus
          fullWidth
          size='small'
          label={labels.nameLabel}
          placeholder={labels.namePlaceholder}
          value={name}
          onChange={(event) => setName(event.target.value)}
          error={nameInvalid}
          helperText={nameInvalid ? labels.nameRequired : " "}
          disabled={submitting}
          inputProps={{ maxLength: 80 }}
          sx={{ mb: 1 }}
        />

        <FormControl fullWidth size='small' sx={{ mb: 2 }} disabled={submitting}>
          <InputLabel id='mcp-expiry-label'>{labels.expiryLabel}</InputLabel>
          <Select
            labelId='mcp-expiry-label'
            label={labels.expiryLabel}
            value={expiry === null ? "never" : expiry}
            onChange={(event) =>
              setExpiry(event.target.value === "never" ? null : event.target.value)
            }
          >
            {expiryOptions.map((option) => (
              <MenuItem
                key={option.label}
                value={option.value === null ? "never" : option.value}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant='subtitle2' fontWeight='bold'>
            {labels.toolsTitle}
          </Typography>
          <Box>
            <Button
              size='small'
              onClick={() => setSelected(toolNames)}
              disabled={submitting || toolsLoading}
            >
              {labels.selectAll}
            </Button>
            <Button
              size='small'
              onClick={() => setSelected([])}
              disabled={submitting || toolsLoading}
            >
              {labels.clearAll}
            </Button>
          </Box>
        </Box>

        {toolsFallback && (
          <Alert severity='info' sx={{ my: 1 }}>
            {labels.toolsFallback}
          </Alert>
        )}

        {toolsLoading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant='body2'>{labels.toolsLoading}</Typography>
          </Box>
        ) : (
          Object.entries(grouped).map(([group, groupTools]) => (
            <Box key={group} sx={{ mt: 1 }}>
              <Typography
                variant='caption'
                sx={{ color: "text.secondary", fontWeight: 600 }}
              >
                {group}
              </Typography>
              {groupTools.map((tool) => (
                <Box
                  key={tool.name}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        size='small'
                        checked={selected.includes(tool.name)}
                        onChange={() => toggleTool(tool.name)}
                        disabled={submitting}
                      />
                    }
                    label={
                      <Typography
                        variant='body2'
                        sx={{ fontFamily: "monospace" }}
                      >
                        {tool.name}
                      </Typography>
                    }
                  />
                  {tool.readOnly && labels.readOnly && (
                    <Chip size='small' variant='outlined' label={labels.readOnly} />
                  )}
                </Box>
              ))}
            </Box>
          ))
        )}

        {toolsInvalid && (
          <FormHelperText error sx={{ mt: 1 }}>
            {labels.toolsRequired}
          </FormHelperText>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={submitting} color='inherit'>
          {labels.cancel}
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={submitting || toolsLoading}
          sx={{ backgroundColor: "#003D78" }}
        >
          {submitting ? labels.submitting : labels.submit}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default McpCreateKeyDialog;
